import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

interface HorizontalScrollerProps {
  children: ReactNode
  // 横並びの行そのもの（flex・gap・余白など）
  className?: string
  // 左右の矢印の位置（行の内側に寄せる量）
  arrowInsetClassName?: { left: string; right: string }
}

// 端の判定の許容幅（行の padding で初期位置が数px ずれても矢印が出ないように）
const EDGE_TOLERANCE_PX = 20

const ArrowButton = ({ direction, className, onClick }: { direction: 'left' | 'right'; className: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className={`absolute ${className} top-1/2 -translate-y-1/2 z-10 w-16 h-16 rounded-full shadow-lg transition-all bg-[#da4574] hover:bg-[#c63a68] cursor-pointer`}
    aria-label={direction === 'left' ? 'Scroll left' : 'Scroll right'}
  >
    <svg className="w-6 h-6 mx-auto text-white" fill="currentColor" viewBox="0 0 448 512">
      {direction === 'left' ? (
        <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z"/>
      ) : (
        <path d="M438.6 278.6c12.5-12.5 12.5-32.8 0-45.3l-160-160c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L338.8 224 32 224c-17.7 0-32 14.3-32 32s14.3 32 32 32l306.7 0L233.4 393.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0l160-160z"/>
      )}
    </svg>
  </button>
)

/**
 * 横スクロールの行。まだスクロールできる方向にだけ矢印を出し、1回でカード1枚分動かす。
 * 子要素はそれぞれ1枚のカード（scroll-snap の吸着先）として扱う。
 */
const HorizontalScroller = ({
  children,
  className = 'flex gap-4 overflow-x-auto scrollbar-hide p-4 scroll-px-4',
  arrowInsetClassName = { left: 'left-2', right: 'right-2' },
}: HorizontalScrollerProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScrollButtons = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setCanScrollLeft(scrollLeft > EDGE_TOLERANCE_PX)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - EDGE_TOLERANCE_PX)
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    checkScrollButtons()
    el.addEventListener('scroll', checkScrollButtons, { passive: true })
    // 行の幅やカードの数・画像の読み込みで中身の幅が変わったときも判定し直す
    const observer = new ResizeObserver(checkScrollButtons)
    observer.observe(el)
    Array.from(el.children).forEach(child => observer.observe(child))
    return () => {
      el.removeEventListener('scroll', checkScrollButtons)
      observer.disconnect()
    }
  }, [children, checkScrollButtons])

  const scroll = (direction: 'left' | 'right') => {
    const el = containerRef.current
    if (!el) return
    const first = el.firstElementChild as HTMLElement | null
    const gap = parseFloat(getComputedStyle(el).columnGap) || 0
    const amount = first ? first.offsetWidth + gap : el.clientWidth
    el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' })
  }

  return (
    <div className="relative">
      {canScrollLeft && (
        <ArrowButton direction="left" className={arrowInsetClassName.left} onClick={() => scroll('left')} />
      )}
      {canScrollRight && (
        <ArrowButton direction="right" className={arrowInsetClassName.right} onClick={() => scroll('right')} />
      )}
      <div
        ref={containerRef}
        className={`${className} *:snap-start`}
        style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch' }}
      >
        {children}
      </div>
    </div>
  )
}

export default HorizontalScroller
